import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";

type InviteRow = {
  id: string;
  invited_email: string;
  status: string;
  created_at: string;
  accepted_at: string | null;
};

type MemberRow = {
  userId: string;
  email: string;
  displayName: string;
  jobRole: string;
  joinedAt: string;
};

type Props = {
  workspaceId: string;
  inviteCode: string;
};

type FilterTab = "all" | "joined" | "pending";

export default function InvitePanel({ workspaceId, inviteCode }: Props) {
  const { user } = useAuth();
  const [emailInput, setEmailInput] = useState("");
  const [bulkInput, setBulkInput] = useState("");
  const [inviteList, setInviteList] = useState<InviteRow[]>([]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // 공유 UI 상태
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const joinUrl = inviteCode
    ? `${window.location.origin}/join?code=${inviteCode}`
    : "";
  const qrImgUrl = joinUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(joinUrl)}&bgcolor=1C1F2E&color=E8375F&margin=10`
    : "";

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingList(true);

    const [{ data: invites }, { data: wm }] = await Promise.all([
      supabase.from("invitations").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: false }),
      supabase.from("workspace_members").select("user_id, job_role, joined_at").eq("workspace_id", workspaceId),
    ]);

    if (invites) setInviteList(invites as InviteRow[]);

    if (wm && wm.length > 0) {
      const ids = wm.map(m => m.user_id);
      const { data: profiles } = await supabase.from("profiles").select("id, email, display_name").in("id", ids);
      const profMap: Record<string, { email: string; display_name: string }> = {};
      (profiles ?? []).forEach(p => { profMap[p.id] = p; });
      setMembers(wm.map(m => ({
        userId: m.user_id,
        email: profMap[m.user_id]?.email ?? "-",
        displayName: profMap[m.user_id]?.display_name ?? "익명",
        jobRole: m.job_role ?? "-",
        joinedAt: m.joined_at,
      })));
    } else {
      setMembers([]);
    }
    setLoadingList(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async () => {
    if (!emailInput.trim() || !user || !workspaceId) return;
    setSending(true);
    const { data, error } = await supabase.from("invitations").upsert({
      workspace_id: workspaceId, invited_email: emailInput.trim(), invited_by: user.id, status: "pending",
    }, { onConflict: "workspace_id,invited_email" }).select().maybeSingle();
    if (!error && data) setInviteList(prev => [data as InviteRow, ...prev.filter(i => i.invited_email !== emailInput.trim())]);
    setEmailInput("");
    setSending(false);
  };

  const handleBulkInvite = async () => {
    const emails = bulkInput.split("\n").map(e => e.trim()).filter(Boolean);
    if (!user || !workspaceId || emails.length === 0) return;
    setSending(true);
    const rows = emails.map(email => ({ workspace_id: workspaceId, invited_email: email, invited_by: user.id, status: "pending" }));
    await supabase.from("invitations").upsert(rows, { onConflict: "workspace_id,invited_email" });
    await fetchData();
    setBulkInput("");
    setSending(false);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(inviteCode).catch(() => {});
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(joinUrl).catch(() => {});
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`K-ArtPEN 워크스페이스 초대`);
    const body = encodeURIComponent(`안녕하세요!\n\nK-ArtPEN 워크스페이스에 초대합니다.\n\n초대 코드: ${inviteCode}\n직접 링크: ${joinUrl}\n\n위 링크를 클릭하거나 초대 코드를 입력하여 입장하세요.`);
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const joinedEmails = new Set(members.map(m => m.email));
  const pendingInvites = inviteList.filter(i => !joinedEmails.has(i.invited_email));
  const joinedInvites = inviteList.filter(i => joinedEmails.has(i.invited_email));
  const uninvitedMembers = members.filter(m => !inviteList.some(i => i.invited_email === m.email));

  const stats = [
    { label: "초대 발송", value: inviteList.length, color: "#7A9BFF", icon: "ri-mail-send-line" },
    { label: "입장 완료", value: members.length, color: "#00C896", icon: "ri-user-follow-line" },
    { label: "미입장", value: pendingInvites.length, color: "#FF9F43", icon: "ri-time-line" },
    { label: "코드 직접입장", value: uninvitedMembers.length, color: "#E8375F", icon: "ri-key-line" },
  ];

  const statusColor = (email: string) => {
    if (joinedEmails.has(email)) return "text-[#00C896] bg-[#00C896]/10 border-[#00C896]/30";
    return "text-[#FF9F43] bg-[#FF9F43]/10 border-[#FF9F43]/30";
  };
  const statusLabel = (email: string) => joinedEmails.has(email) ? "입장 완료" : "미입장";

  const filteredList = filterTab === "all"
    ? inviteList
    : filterTab === "joined"
      ? joinedInvites
      : pendingInvites;

  return (
    <div className="space-y-5">
      {/* 초대 코드 카드 */}
      <div className="bg-gradient-to-r from-[#E8375F]/15 to-[#1C1F2E] rounded-xl border border-[#E8375F]/30 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[#5A5E72] text-xs">워크스페이스 초대 코드</div>
          <button
            type="button"
            onClick={() => setShowSharePanel(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
              showSharePanel
                ? "bg-[#E8375F]/20 text-[#E8375F] border-[#E8375F]/40"
                : "border-[#2A2D3E] text-[#5A5E72] hover:text-[#888888] hover:border-[#3A3D4E]"
            }`}
          >
            <i className="ri-share-line text-sm"></i>
            공유하기
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 bg-[#252830] rounded-lg px-4 py-2.5 font-mono text-white text-lg font-bold tracking-widest">
            {inviteCode || "워크스페이스를 선택하세요"}
          </div>
          <button
            type="button"
            onClick={handleCopyCode}
            disabled={!inviteCode}
            className="px-4 py-2.5 bg-[#E8375F] text-white text-sm rounded-lg hover:bg-[#FF4B7A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {codeCopied ? <><i className="ri-check-line mr-1"></i>복사됨</> : <><i className="ri-clipboard-line mr-1"></i>코드 복사</>}
          </button>
        </div>

        <div className="text-[#5A5E72] text-xs mt-2">학생들이 이 코드를 입력하면 워크스페이스에 입장합니다.</div>

        {/* 공유 패널 */}
        {showSharePanel && inviteCode && (
          <div className="mt-4 pt-4 border-t border-[#E8375F]/20">
            <div className="grid grid-cols-2 gap-4">
              {/* QR 코드 */}
              <div className="flex flex-col items-center">
                <div className="text-[#5A5E72] text-[10px] uppercase tracking-widest mb-2">QR 코드 스캔</div>
                <div className="w-32 h-32 bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] overflow-hidden flex items-center justify-center">
                  {qrImgUrl ? (
                    <img
                      src={qrImgUrl}
                      alt="초대 QR 코드"
                      className="w-full h-full object-contain p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <i className="ri-qr-code-line text-3xl text-[#3E4155]"></i>
                  )}
                </div>
                <div className="text-[#3E4155] text-[10px] mt-2 text-center">카메라로 스캔하면<br />바로 입장 가능</div>
              </div>

              {/* 공유 버튼들 */}
              <div className="space-y-2.5">
                <div className="text-[#5A5E72] text-[10px] uppercase tracking-widest mb-2">링크 공유</div>

                {/* 입장 URL */}
                <div className="bg-[#1C1F2E] rounded-lg px-3 py-2 border border-[#2A2D3E]">
                  <div className="text-[#3E4155] text-[9px] mb-0.5">입장 링크</div>
                  <div className="text-[#888888] text-[10px] font-mono truncate">{joinUrl}</div>
                </div>

                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2D3E] hover:border-[#E8375F]/30 transition-colors cursor-pointer whitespace-nowrap group"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${urlCopied ? "ri-check-line text-[#00C896]" : "ri-link text-[#5A5E72] group-hover:text-[#888888]"} text-sm transition-colors`}></i>
                  </div>
                  <span className={`text-xs ${urlCopied ? "text-[#00C896]" : "text-[#5A5E72] group-hover:text-[#888888]"} transition-colors`}>
                    {urlCopied ? "링크 복사됨!" : "링크 복사"}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={handleEmailShare}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2D3E] hover:border-[#7A9BFF]/30 transition-colors cursor-pointer whitespace-nowrap group"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-[#5A5E72] group-hover:text-[#7A9BFF] text-sm transition-colors"></i>
                  </div>
                  <span className="text-xs text-[#5A5E72] group-hover:text-[#7A9BFF] transition-colors">이메일로 공유</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const text = `K-ArtPEN 워크스페이스 초대\n초대 코드: ${inviteCode}\n입장 링크: ${joinUrl}`;
                    navigator.clipboard.writeText(text).catch(() => {});
                    setUrlCopied(true);
                    setTimeout(() => setUrlCopied(false), 2000);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2A2D3E] hover:border-[#00C896]/30 transition-colors cursor-pointer whitespace-nowrap group"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-chat-3-line text-[#5A5E72] group-hover:text-[#00C896] text-sm transition-colors"></i>
                  </div>
                  <span className="text-xs text-[#5A5E72] group-hover:text-[#00C896] transition-colors">메시지용 텍스트 복사</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 요약 통계 */}
      <div className="grid grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-[#1C1F2E] border border-[#2A2D3E] rounded-xl px-4 py-3">
            <div className="flex items-center gap-1.5 mb-1">
              <i className={`${s.icon} text-xs`} style={{ color: s.color }}></i>
              <span className="text-[#5A5E72] text-[10px]">{s.label}</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-[#3E4155] text-[9px]">명</div>
          </div>
        ))}
      </div>

      {/* 미입장 학생 강조 패널 */}
      {pendingInvites.length > 0 && (
        <div className="bg-[#FF9F43]/[0.06] border border-[#FF9F43]/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-time-line text-[#FF9F43] text-sm"></i>
            <span className="text-[#FF9F43] text-xs font-semibold">미입장 학생 {pendingInvites.length}명</span>
            <span className="text-[#5A5E72] text-[10px]">— 아직 워크스페이스에 접속하지 않은 학생들</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {pendingInvites.slice(0, 8).map(inv => (
              <div key={inv.id} className="flex items-center gap-1.5 bg-[#252830] border border-[#FF9F43]/20 rounded-lg px-2.5 py-1.5">
                <div className="w-5 h-5 rounded-full bg-[#FF9F43]/15 flex items-center justify-center text-[#FF9F43] text-[9px] font-bold">
                  {inv.invited_email[0]?.toUpperCase()}
                </div>
                <span className="text-[#888888] text-[10px] max-w-[140px] truncate">{inv.invited_email}</span>
                <span className="text-[#5A5E72] text-[9px]">
                  {new Date(inv.created_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })} 초대
                </span>
              </div>
            ))}
            {pendingInvites.length > 8 && (
              <div className="flex items-center px-2.5 py-1.5 text-[#5A5E72] text-[10px]">
                +{pendingInvites.length - 8}명 더
              </div>
            )}
          </div>
        </div>
      )}

      {/* 초대 양식 */}
      <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-5">
        <div className="flex items-center gap-1 mb-4 bg-[#252830] rounded-lg p-1 w-fit">
          {(["single", "bulk"] as const).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === m ? "bg-[#1C1F2E] text-white" : "text-[#5A5E72]"}`}>
              {m === "single" ? "개별 초대" : "일괄 초대"}
            </button>
          ))}
        </div>
        {mode === "single" ? (
          <div className="flex gap-2">
            <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleInvite()}
              placeholder="학생 이메일 입력..."
              className="flex-1 bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder:text-[#3E4155]" />
            <button type="button" onClick={handleInvite} disabled={sending || !workspaceId}
              className="px-4 py-2.5 bg-[#E8375F] text-white text-sm rounded-lg hover:bg-[#FF4B7A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              {sending ? "발송 중..." : "초대 등록"}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)}
              placeholder={"이메일을 줄바꿈으로 구분\nstudent01@school.ac.kr\nstudent02@school.ac.kr"}
              rows={4} maxLength={500}
              className="w-full bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder:text-[#3E4155] resize-none" />
            <button type="button" onClick={handleBulkInvite} disabled={sending || !workspaceId}
              className="w-full py-2.5 bg-[#E8375F] text-white text-sm rounded-lg hover:bg-[#FF4B7A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              {sending ? "발송 중..." : "일괄 초대 등록"}
            </button>
          </div>
        )}
      </div>

      {/* 초대 목록 */}
      <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] overflow-hidden">
        <div className="px-5 py-3 border-b border-[#2A2D3E] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[#111111] text-sm font-semibold">초대 현황</span>
            <div className="flex gap-1">
              {([["all", "전체"], ["joined", "입장완료"], ["pending", "미입장"]] as const).map(([tab, label]) => (
                <button key={tab} type="button" onClick={() => setFilterTab(tab)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    filterTab === tab
                      ? "bg-[#E8375F]/15 text-[#E8375F] border border-[#E8375F]/30"
                      : "text-[#5A5E72] hover:text-[#888888]"
                  }`}>
                  {label}
                  {tab === "pending" && pendingInvites.length > 0 && (
                    <span className="ml-1 bg-[#FF9F43]/20 text-[#FF9F43] px-1 rounded-full">{pendingInvites.length}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={fetchData}
            className="w-6 h-6 flex items-center justify-center text-[#3E4155] hover:text-white transition-colors cursor-pointer">
            <i className={`ri-refresh-line text-xs ${loadingList ? "animate-spin" : ""}`}></i>
          </button>
        </div>

        {loadingList ? (
          <div className="px-5 py-8 text-center text-[#5A5E72] text-sm animate-pulse">로딩 중...</div>
        ) : filteredList.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <i className="ri-user-add-line text-2xl text-[#2A2A2A] block mb-3"></i>
            <p className="text-[#5A5E72] text-sm">
              {filterTab === "pending" ? "미입장 학생이 없어요 🎉" : "초대한 학생이 없어요"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#252830]">
            {filteredList.map((inv) => {
              const member = members.find(m => m.email === inv.invited_email);
              const isJoined = joinedEmails.has(inv.invited_email);
              return (
                <div key={inv.id} className="px-5 py-3 flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                    isJoined ? "bg-[#00C896]/15 text-[#00C896]" : "bg-[#FF9F43]/10 text-[#FF9F43]"
                  }`}>
                    {inv.invited_email.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#CCCCCC] text-xs font-medium truncate">
                      {member?.displayName ?? inv.invited_email}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[#5A5E72] text-[10px] truncate">{inv.invited_email}</span>
                      {isJoined && member && (
                        <span className="text-[#3E4155] text-[9px] shrink-0">
                          · {member.jobRole !== "-" ? member.jobRole : "직무 미선택"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 space-y-0.5">
                    <div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusColor(inv.invited_email)}`}>
                        {statusLabel(inv.invited_email)}
                      </span>
                    </div>
                    <div className="text-[#3E4155] text-[9px]">
                      {isJoined && member
                        ? `${new Date(member.joinedAt).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })} 입장`
                        : `${new Date(inv.created_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })} 초대`
                      }
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 초대코드 직접 입장 멤버 */}
        {uninvitedMembers.length > 0 && filterTab === "all" && (
          <div className="border-t border-[#2A2D3E]">
            <div className="px-5 py-2 bg-[#E8375F]/[0.04]">
              <span className="text-[#E8375F]/60 text-[10px] font-semibold">
                <i className="ri-key-line mr-1"></i>초대 없이 코드로 직접 입장한 멤버 {uninvitedMembers.length}명
              </span>
            </div>
            {uninvitedMembers.map(m => (
              <div key={m.userId} className="px-5 py-3 flex items-center gap-3 border-t border-[#252830]">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#E8375F]/10 text-[#E8375F] text-xs font-bold shrink-0">
                  {m.displayName[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#CCCCCC] text-xs font-medium truncate">{m.displayName}</div>
                  <div className="text-[#5A5E72] text-[10px] truncate">{m.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border text-[#00C896] bg-[#00C896]/10 border-[#00C896]/30">
                    입장 완료
                  </span>
                  <div className="text-[#3E4155] text-[9px] mt-0.5">
                    {new Date(m.joinedAt).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })} 입장
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
